import React, { useState, useEffect } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { 
  FiShoppingCart, FiPackage, FiDollarSign, FiUsers, 
  FiAlertCircle, FiTrendingUp, FiCheckCircle, FiActivity, 
  FiAward, FiArrowUpRight, FiZap
} from "react-icons/fi";
import { baseurl } from "../baseurl";
import axios from "axios";

function Home() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const res = await axios.get(`${baseurl}getAdminDashboardInsights`);
        if (res.data.success) setData(res.data.data);
      } catch (err) {
        console.error("Dashboard Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  if (loading || !data) {
    return (
      <div className="loader-container">
        <div className="spinner-grow text-primary" role="status"></div>
      </div>
    );
  }

  const { summary, alerts, analytics, recentActivity } = data;

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-custom-light">
        <div className="main-content p-3 p-lg-4">
          
          {/* Header Section */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 className="fw-bold mb-0 text-dark">Intelligence Dashboard</h4>
              <p className="text-muted small mb-0">Real-time platform performance metrics</p>
            </div>
            {/* <button className="btn btn-dark btn-sm rounded-3 px-3 py-2 shadow-sm d-flex align-items-center">
              <FiActivity className="me-2" /> Live Report
            </button> */}
          </div>

          {/* KPI Mini-Cards */}
          <div className="row g-3 mb-4">
            {[
              { label: "Revenue", val: `₹${summary.revenue}`, icon: <FiDollarSign />, color: "#6366f1", trend: "+12%" },
              { label: "Orders", val: summary.orders, icon: <FiShoppingCart />, color: "#0ea5e9", trend: "+5%" },
              { label: "Customers", val: summary.customers, icon: <FiUsers />, color: "#10b981", trend: "+18%" },
              { label: "Vendors", val: summary.vendors, icon: <FiPackage />, color: "#f59e0b", trend: "Steady" },
            ].map((kpi, i) => (
              <div className="col-6 col-md-3" key={i}>
                <div className="kpi-card h-100 shadow-sm border-0 card p-3 rounded-4">
                  <div className="d-flex justify-content-between mb-2">
                    <div className="icon-box" style={{ backgroundColor: `${kpi.color}15`, color: kpi.color }}>
                      {kpi.icon}
                    </div>
                    <span className="trend-label text-success small fw-bold">
                        <FiArrowUpRight /> {kpi.trend}
                    </span>
                  </div>
                  <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>{kpi.label}</small>
                  <h4 className="fw-bold mb-0 mt-1">{kpi.val}</h4>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-3 mb-4">
            {/* Main Trend Chart */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm rounded-4 p-3 h-100">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="fw-bold mb-0">Revenue Trajectory</h6>
                  <select className="form-select form-select-sm w-auto border-0 bg-light fw-bold">
                    <option>Last 6 Months</option>
                  </select>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={analytics.revenueHistory}>
                    <defs>
                      <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#94a3b8'}} />
                    <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#94a3b8'}} />
                    <Tooltip contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'}} />
                    <Line type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={3} dot={{ fill: '#6366f1', strokeWidth: 2, r: 4, stroke: '#fff' }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Side Status & Brands */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                <h6 className="fw-bold mb-3 d-flex align-items-center"><FiAlertCircle className="me-2 text-danger" /> Critical Alerts</h6>
                <div className="alert-item d-flex align-items-center justify-content-between p-2 mb-2 rounded-3 bg-light-danger">
                    <span className="small fw-semibold">Out of Stock</span>
                    <span className="badge bg-danger rounded-pill">{alerts.outOfStock}</span>
                </div>
                <div className="alert-item d-flex align-items-center justify-content-between p-2 mb-2 rounded-3 bg-light-warning">
                    <span className="small fw-semibold">Low Stock Items</span>
                    <span className="badge bg-warning text-dark rounded-pill">{alerts.lowStock}</span>
                </div>
                <div className="alert-item d-flex align-items-center justify-content-between p-2 rounded-3 bg-light-primary">
                    <span className="small fw-semibold">Approvals Needed</span>
                    <span className="badge bg-primary rounded-pill">{alerts.pendingApprovals}</span>
                </div>
              </div>

              <div className="card border-0 shadow-sm rounded-4 p-3">
                <h6 className="fw-bold mb-3">Inventory Distribution</h6>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie data={[{ name: 'COD', value: analytics.paymentMethods.cod || 0 }, { name: 'Online', value: analytics.paymentMethods.online || 0 }]} innerRadius={45} outerRadius={60} paddingAngle={8} dataKey="value">
                      <Cell fill="#6366f1" />
                      <Cell fill="#f1f5f9" stroke="#e2e8f0" />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="d-flex justify-content-center gap-3 mt-1">
                  <small className="fw-bold text-muted"><span className="dot" style={{background: '#6366f1'}}></span> COD</small>
                  <small className="fw-bold text-muted"><span className="dot" style={{background: '#cbd5e1'}}></span> Online</small>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            {/* Recent Orders Compact */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white">
                  <h6 className="fw-bold mb-0">Recent Transactions</h6>
                  <button className="btn btn-link btn-sm text-decoration-none fw-bold">View All</button>
                </div>
                <div className="table-responsive">
                  <table className="table align-middle mb-0 custom-table">
                    <thead>
                      <tr>
                        <th className="ps-3">ID</th>
                        <th>Client</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th className="text-end pe-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentActivity.orders.map((order, i) => (
                        <tr key={i}>
                          <td className="ps-3"><span className="text-muted font-monospace small">#{order.order_uuid.substring(0, 5)}</span></td>
                          <td><div className="fw-bold small">{order.customer_name}</div><div className="text-muted extra-small">{new Date(order.created_at).toLocaleDateString()}</div></td>
                          <td className="fw-bold small">₹{order.amount}</td>
                          <td>
                            <span className={`status-pill ${order.order_status.toLowerCase()}`}>
                              {order.order_status}
                            </span>
                          </td>
                          <td className="text-end pe-3">
                            <button className="btn btn-icon-sm"><FiZap /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Top Vendor Compact */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm rounded-4 p-3 bg-midnight text-white">
                <div className="d-flex align-items-center mb-3">
                    <FiAward className="text-warning me-2" size={20} />
                    <h6 className="fw-bold mb-0 text-white">Star Performer</h6>
                </div>
                {analytics.topVendors.slice(0, 1).map((vendor, idx) => (
                  <div key={idx} className="text-center py-2">
                    <div className="vendor-avatar mb-3 mx-auto">
                        <span className="h4 mb-0 fw-bold text-white">{vendor.business_name.charAt(0)}</span>
                    </div>
                    <h5 className="mb-1 fw-bold text-white">{vendor.business_name}</h5>
                    <p className=" small mb-3 text-white">{vendor.vendor_name}</p>
                    <div className="sales-box bg-white-10 rounded-3 p-2">
                      <small className="d-block text-white">Monthly Sales Volume</small>
                      <h4 className="mb-0 fw-bold text-white">₹{vendor.total_sales}</h4>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </main>

      <style>{`
        .bg-custom-light { background-color: #f8fafc; min-height: 100vh; }
        .loader-container { height: 100vh; display: flex; align-items: center; justify-content: center; background: #fff; }
        .kpi-card { transition: transform 0.2s ease; cursor: pointer; background: #fff; }
        .kpi-card:hover { transform: translateY(-5px); }
        .icon-box { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; border-radius: 12px; font-size: 1.2rem; }
        .dot { height: 8px; width: 8px; border-radius: 50%; display: inline-block; }
        
        /* Status Pills */
        .status-pill { padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
        .status-pill.pending { background: #fef3c7; color: #d97706; }
        .status-pill.success { background: #dcfce7; color: #15803d; }
        
        /* Table Styles */
        .custom-table thead th { background: #f8fafc; color: #64748b; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; padding: 12px 8px; border: none; }
        .custom-table tbody td { border-bottom: 1px solid #f1f5f9; padding: 12px 8px; }
        .extra-small { font-size: 10px; }
        .btn-icon-sm { width: 30px; height: 30px; padding: 0; border-radius: 8px; border: 1px solid #e2e8f0; background: #fff; color: #64748b; }
        .btn-icon-sm:hover { background: #6366f1; color: #fff; border-color: #6366f1; }

        /* Alerts Backgrounds */
        .bg-light-danger { background: #fff1f2; color: #e11d48; }
        .bg-light-warning { background: #fffbeb; color: #d97706; }
        .bg-light-primary { background: #eef2ff; color: #4f46e5; }

        /* Dark Card for Vendor */
        .bg-midnight { background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%); }
        .bg-white-10 { background: rgba(255,255,255,0.1); }
        .vendor-avatar { width: 60px; height: 60px; border-radius: 50%; background: #6366f1; display: flex; align-items: center; justify-content: center; border: 4px solid rgba(255,255,255,0.1); }
      `}</style>
    </>
  );
}

export default Home;